import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import { NavigateFunction } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { AuthenticatedRoutes } from '@/router/routes';

import { DepartmentFormData } from '../types/department';
import { DepartmentsFormData } from '../validations/department-validation';

interface GetColumnsParams {
  onDelete: (department: DepartmentFormData, index: number) => void;
  control: Control<DepartmentsFormData>;
  t: TFunction;
  navigate: NavigateFunction;
}

export const getDepartmentsColumns = ({
  onDelete,
  control,
  t,
  navigate,
}: GetColumnsParams): ColumnDef<DepartmentFormData>[] => [
  {
    accessorKey: 'name',
    header: () => {
      return <div style={{ minWidth: '16rem' }}>{t('pages.departments.nameColumn')}</div>;
    },
    cell: ({ row }) => {
      const index = row.index;

      return (
        <FormField
          control={control}
          name={`departments.${index}.name`}
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('pages.departments.namePlaceholder')}
                  className={cn(
                    fieldState.error && 'border-destructive focus-visible:ring-destructive'
                  )}
                />
              </FormControl>
              {fieldState.error && (
                <FormMessage>{t(fieldState.error.message as string)}</FormMessage>
              )}
            </FormItem>
          )}
        />
      );
    },
  },
  {
    accessorKey: 'memberCount',
    header: () => {
      return <div style={{ minWidth: '12rem' }}>{t('pages.departments.membersColumn')}</div>;
    },
    cell: ({ row }) => {
      const department = row.original;
      const hasMembers = department.memberCount > 0;

      const isNewDepartment = department.departmentId < 0;

      return (
        <Button
          type="button"
          variant="ghost"
          disabled={!hasMembers || isNewDepartment}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            if (hasMembers && !isNewDepartment) {
              navigate(`${AuthenticatedRoutes.Team}?department=${department.departmentId}`);
            }
          }}
          className="h-auto w-full justify-start rounded-lg px-3 py-2"
        >
          {department.memberCount}
        </Button>
      );
    },
  },
  {
    id: 'actions',
    size: 80,
    header: () => {
      return <div className="flex w-full justify-end">{t('pages.departments.actionsColumn')}</div>;
    },
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original, row.index)}
            className="h-8 w-8"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    },
  },
];
